import { useHistory } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Button, Card, Empty, Grid, Modal, Space, Table, Tag, Typography, Select } from "antd";
import { t } from "i18next";
import {
    PlusOutlined,
    ReloadOutlined,
    EyeOutlined,
    DeleteOutlined,
    BankOutlined
} from "@ant-design/icons";
import SimpleLayout from "../../core/components/layout/SimpleLayout";
import ApiService from "../../core/service/ApiService";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import { useModal } from "../../core/context/ModalContext";
import InstitutionCreateEdit from "./InstitutionCreateEdit";
import { FilterActions, FilterCard, SearchCard } from "../../core/components/quick-filter/QuickFilterParts";

export default function InstitutionList() {
    const history = useHistory();
    const { openModal, closeModal } = useModal()
    const { firmSettings, user, hasPermission } = useUser();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    const [institutions, setInstitutions] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [enableFilter, setEnableFilter] = useState(true);
    const { Option } = Select;

    const getInstitutions = async (enabled) => {
        const res = await ApiService.Institution.GetInstitutionList(enabled);

        if (res.success) {
            setInstitutions(res.data);
        }
    }

    const deleteInstitution = async (institutionId) => {
        const res = await ApiService.Institution.DeleteInstitution(institutionId)
        if (res.success) {
            await getInstitutions();
        }
    }

    useEffect(() => {
        getInstitutions(enableFilter)
    }, [])

    const editInstitution = async (institutionId) => {
        openModal(t(''), (
            <InstitutionCreateEdit
                showFirmSettings={true}
                institutionId={institutionId}
                editable={hasAccess(institutionId)}
                onOk={(id) => {
                    closeModal()
                    editInstitution(id)
                }}
            />
        ), async () => {
            await getInstitutions(enableFilter);
            closeModal()
        })
    }

    const hasAccess = (institutionId) => {
        if (
            user?.institutionFirms?.find(x => x.institutionId === institutionId)
            || hasPermission(PERMISSIONS.FIRM_INSTITUTION_MANAGER)
        )
            return true;
    }

    const renderAction = (row) => {
        return (
            <Space>
                <a key="edit" onClick={() => editInstitution(row.id)}>{t('Show')}</a>
                {
                    hasPermission(PERMISSIONS.FIRM_INSTITUTION_MANAGER) && (
                        <a key="delete" onClick={async () => {
                            await deleteInstitution(row.id)
                        }} style={{ color: 'red' }}>{t('Delete')}</a>
                    )
                }
            </Space>
        )
    }

    const confirmDelete = (row) => {
        Modal.confirm({
            title: t('Delete'),
            content: `${row.name} silinsin mi?`,
            okText: t('Yes'),
            cancelText: t('No'),
            okButtonProps: { danger: true },
            onOk: async () => { await deleteInstitution(row.id) }
        });
    };

    const filteredData = useMemo(() => {
        if (!searchText) return institutions;

        const search = searchText.toLocaleUpperCase(firmSettings?.locale);
        return institutions.filter(x =>
            x.name?.toLocaleUpperCase(firmSettings?.locale).includes(search)
        )
    }, [institutions, searchText, firmSettings?.locale])

    // --- MOBİL KART RENDER ---
    const renderMobileCard = (row) => {
        const canDelete = hasPermission(PERMISSIONS.FIRM_INSTITUTION_MANAGER);

        return (
            <Card
                key={row.id}
                size="small"
                bodyStyle={{ padding: 12 }}
                style={{
                    borderRadius: 6,
                    borderLeft: '4px solid #fa8c16',
                    marginBottom: 10
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                            <BankOutlined style={{ color: '#fa8c16', flexShrink: 0 }} />
                            <span style={{ fontWeight: 600, fontSize: 14, color: '#262626', wordBreak: 'break-word' }}>
                                {row.name}
                            </span>
                        </div>
                        {row?.institutionType?.name && (
                            <Tag style={{ fontSize: 11, margin: 0, padding: '0 6px' }}>
                                {row.institutionType.name}
                            </Tag>
                        )}
                    </div>
                </div>

                <div style={{
                    display: 'flex',
                    gap: 8,
                    borderTop: '1px dashed #f0f0f0',
                    paddingTop: 8
                }}>
                    <Button
                        size="middle"
                        icon={<EyeOutlined />}
                        onClick={() => editInstitution(row.id)}
                        style={{ flex: 1 }}
                    >
                        {t('Show')}
                    </Button>
                    {canDelete && (
                        <Button
                            size="middle"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => confirmDelete(row)}
                            style={{ flex: '0 0 50px' }}
                        />
                    )}
                </div>
            </Card>
        );
    };

    // --- FİLTRE ÇUBUĞU (randevu listesiyle aynı düzen: arama + filtre kartları, altında eylemler) ---
    const renderFilters = () => (
        <>
            <div className="qf-row qf-row-top">
                <SearchCard value={searchText} onChange={setSearchText} />
                <FilterCard label={t('Enable')}>
                    <Select
                        allowClear
                        size="small"
                        placeholder={t('Enable')}
                        style={{ width: '100%' }}
                        value={enableFilter}
                        onChange={(value) => {
                            setEnableFilter(value);
                            getInstitutions(value);
                        }}
                    >
                        <Option value={true}>{t('Active')}</Option>
                        <Option value={false}>{t('Passive')}</Option>
                    </Select>
                </FilterCard>
            </div>
            <FilterActions>
                <Button size="small" icon={<PlusOutlined />} onClick={() => editInstitution()} type="primary">
                    {t('New')}
                </Button>
                <Button size="small" icon={<ReloadOutlined />} onClick={() => getInstitutions(enableFilter)}>
                    {t('Refresh')}
                </Button>
            </FilterActions>
        </>
    );

    return (
        <SimpleLayout fullWidth={true}>
            {renderFilters()}
            <Card bodyStyle={{ padding: isMobile ? 12 : 24 }}>

                {isMobile ? (
                    <div>
                        {filteredData.length === 0 ? (
                            <Empty description={t('No Data')} style={{ padding: 32 }} />
                        ) : (
                            <>
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
                                    {filteredData.length} kayıt
                                </div>
                                {filteredData.map(row => renderMobileCard(row))}
                            </>
                        )}
                    </div>
                ) : (
                    <Table
                        size={'small'}
                        dataSource={filteredData}
                        rowKey={'id'}
                        columns={[
                            { key: 'name', dataIndex: 'name', title: t('Name') },
                            { key: 'type', dataIndex: 'type', title: t('Type'), render: (text, row) => row?.institutionType?.name },
                            { key: '#', dataIndex: '#', title: '#', render: (text, row) => renderAction(row) },
                        ]}
                    />
                )}
            </Card>
        </SimpleLayout>
    )
}
